import { borderRadius, color, Icon, space, typography } from '@primer-io/goat'
import React, { useState, useMemo, useEffect, useRef } from 'react'
import styled from 'styled-components'
import useWindowHash from 'hooks/useWindowHash'
import { useItemContext, ItemContextProvider } from '../Item/ItemContext'
import slugify from 'slugify'
import { Minus, Plus } from '@primer-io/goat-icons'

const shouldOpen = (path, hash) => !!hash && !!path && hash !== path && hash?.startsWith?.(path)

/**
 * This function takes a DOM element and returns the list of h2 > h3 > h4 > h5 > h6 that the DOM element is in.
 * If you consider the DOM to be a book, this function takes a piece of text and returns the Part / Chapter it's in.
 * The list is then squashed to the format `h2id.h3id.h4id.h5id.h6id`.
 */
const getBasePath = el =>
	Array.from(document.querySelectorAll('h2, h3, h4, h5, h6'))
		.filter(header => header.compareDocumentPosition(el) != Node.DOCUMENT_POSITION_PRECEDING)
		.filter(element => element.id)
		.reduce((acc, header) => {
			const depth = Number(header.tagName.substring(1))
			acc[depth] = header.id
			return acc
		}, [])
		.filter(Boolean)
		.join('.')

/** @param {{ children: React.ReactNode; open?: boolean; summary?: string }} */
export function Group({ children, open, summary, path }) {
	const containerRef = useRef()
	const detailsRef = useRef()
	const [basePath, setBasePath] = useState()

	const hash = useWindowHash()
	const { path: parentPath } = useItemContext()

	const appendedPath = [basePath, summary && slugify(summary, { lower: true })].filter(Boolean).join('-')
	const defaultOpen = open || shouldOpen(appendedPath, hash)

	// Open needs to be done client-side
	useEffect(() => {
		if (!detailsRef.current) {
			return
		}

		if (defaultOpen) {
			detailsRef.current.open = true
		}
	}, [detailsRef, defaultOpen])

	useEffect(() => {
		if (path) {
			setBasePath(path)
			return
		}

		if (parentPath) {
			setBasePath(parentPath)
			return
		}

		if (!containerRef.current && detailsRef.current) {
			return
		}

		const basePath = getBasePath(containerRef.current ?? detailsRef.current)
		setBasePath(basePath)
	}, [path, parentPath])

	const content = <ItemContextProvider path={appendedPath}>{children}</ItemContextProvider>

	if (!summary) {
		return <Container ref={containerRef}>{content}</Container>
	}

	return (
		<Details ref={detailsRef}>
			<Summary>
				{summary}
				<Icon className='minus' as={Minus} size='small' />
				<Icon className='plus' as={Plus} size='small' />
			</Summary>
			{content}
		</Details>
	)
}

const Container = styled.div`
	border: 1px solid ${color('gray-300')};
	border-radius: ${borderRadius('medium')};
	margin-top: ${space(6)};

	> * + * {
		border-top: 1px solid ${color('gray-300')};
	}
`

const Details = styled(Container).attrs({ as: 'details' })`
	&:not([open]) > summary > .minus {
		display: none;
	}

	&[open] > summary > .plus {
		display: none;
	}
`

const Summary = styled.summary`
	${typography('title-medium')};
	background-color: ${color('gray-50')};
	border-radius: ${borderRadius('medium')};
	cursor: pointer;
	display: flex;
	list-style: none;
	padding: ${space(2)} ${space(4)};
	place-content: space-between;
	place-items: center;
	transition: background-color 0.1s;

	&::-webkit-details-marker {
		display: none;
	}

	&:hover {
		background-color: ${color('gray-100')};
	}

	&:active {
		background-color: ${color('gray-200')};
	}
`
